import React, { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Autocomplete,
    Button,
    Grid,
    Pagination,
    PaginationItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useStyle } from '../style';
import { setSnackbar } from '../../../../../components/hooks/snackBar/reducers';
import Loader from '../../../../../components/loader/loader';

const pageOption = [10, 25, 50, 100];

const formatDate = value => (value ? dayjs(value).format('DD-MMM-YYYY') : '-');

const formatAmount = value => Math.round(Number(value) || 0);

const ProductGrnReport = () => {
    const dispatch = useDispatch();
    const classes = useStyle();

    // This report is restricted to Admin / Super Admin, same role_id
    // convention used to gate the outlet picker on the branch-wise sales
    // report (role_id 1 = Super Admin, 2 = Admin).
    const roleId = localStorage.getItem('role_id');
    const isAuthorized = roleId === '1' || roleId === '2';

    const [productList, setProductList] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
    const [toDate, setToDate] = useState(dayjs().endOf('month'));
    const [products, setProducts] = useState([]);
    const [grandTotalQty, setGrandTotalQty] = useState(0);
    const [grandTotalValue, setGrandTotalValue] = useState(0);
    const [isLoader, setIsLoader] = useState(false);
    const [searched, setSearched] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageOption[0]);

    useEffect(() => {
        if (!isAuthorized) return;
        const fetchProductList = async () => {
            setOptionsLoading(true);
            try {
                const response = await axios.get('/v1/reports/product/grn/products');
                setProductList(response.data.products || []);
            } catch (error) {
                setProductList([]);
                dispatch(setSnackbar(true, 'error', 'Failed to load product list'));
            } finally {
                setOptionsLoading(false);
            }
        };
        fetchProductList();
        fetchReport({});
    }, []);

    const productOptions = productList;

    const subCategoryOptions = useMemo(() => {
        const seen = new Set();
        return productList
            .filter(p => {
                if (!p.subcategory_name || seen.has(p.subcategory_name)) return false;
                seen.add(p.subcategory_name);
                return true;
            })
            .map(p => p.subcategory_name);
    }, [productList]);

    const buildDateParams = ({ fromDateOverride, toDateOverride } = {}) => {
        const effectiveFrom = fromDateOverride !== undefined ? fromDateOverride : fromDate;
        const effectiveTo = toDateOverride !== undefined ? toDateOverride : toDate;
        const params = {};
        if (effectiveFrom) params.from_date = dayjs(effectiveFrom).format('YYYY-MM-DD');
        if (effectiveTo) params.to_date = dayjs(effectiveTo).format('YYYY-MM-DD');
        return params;
    };

    const fetchReport = async (filterParams, dateOverrides) => {
        setIsLoader(true);
        try {
            const response = await axios.get('/v1/reports/product/grn', {
                params: { ...filterParams, ...buildDateParams(dateOverrides) }
            });
            const result = response.data.products || [];
            setProducts(result);
            setGrandTotalQty(result.reduce((sum, p) => sum + (Number(p.total_qty_received) || 0), 0));
            setGrandTotalValue(result.reduce((sum, p) => sum + (Number(p.total_value) || 0), 0));
            setPage(0);
        } catch (error) {
            setProducts([]);
            setGrandTotalQty(0);
            setGrandTotalValue(0);
            const message =
                error?.response?.status === 404
                    ? 'No GRN records found for the selected product'
                    : error?.response?.data?.errors?.[0]?.message || 'Failed to fetch GRN report';
            dispatch(setSnackbar(true, 'error', message));
        } finally {
            setIsLoader(false);
            setSearched(true);
        }
    };

    const activeFilterParams = () => {
        if (selectedProduct) return { product_id: selectedProduct.product_id };
        if (selectedSubCategory) return { subcategory_name: selectedSubCategory };
        return null;
    };

    const handleProduct = (e, value) => {
        setSelectedProduct(value);
        setSelectedSubCategory(null);
        fetchReport(value ? { product_id: value.product_id } : {});
    };

    const handleSubCategory = (e, value) => {
        setSelectedSubCategory(value);
        setSelectedProduct(null);
        fetchReport(value ? { subcategory_name: value } : {});
    };

    const handleDateChange = (value, name) => {
        if (name === 'fromDate') setFromDate(value);
        else setToDate(value);

        const filterParams = activeFilterParams() || {};
        const nextFromDate = name === 'fromDate' ? value : fromDate;
        const nextToDate = name === 'toDate' ? value : toDate;
        fetchReport(filterParams, { fromDateOverride: nextFromDate, toDateOverride: nextToDate });
    };

    const clearData = () => {
        setSelectedProduct(null);
        setSelectedSubCategory(null);
        const defaultFrom = dayjs().startOf('month');
        const defaultTo = dayjs().endOf('month');
        setFromDate(defaultFrom);
        setToDate(defaultTo);
        fetchReport({}, { fromDateOverride: defaultFrom, toDateOverride: defaultTo });
    };

    const pagedProducts = products.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const pageCount = Math.ceil(products.length / rowsPerPage);

    const exportToExcel = async () => {
        const filterParams = activeFilterParams() || {};
        setExporting(true);
        setIsLoader(true);
        try {
            const response = await axios.get('/v1/reports/product/grn/export', {
                params: { ...filterParams, ...buildDateParams() }
            });
            const exportProducts = response.data.products || [];

            const header = [
                'Sub-Category / Short Name',
                'Total GRNs',
                'GRN Number',
                'GRN Date',
                'Supplier Name',
                'Quantity Received',
                'Total Value'
            ];

            const appliedFrom = fromDate ? dayjs(fromDate).format('DD-MM-YYYY') : '-';
            const appliedTo = toDate ? dayjs(toDate).format('DD-MM-YYYY') : '-';
            const appliedFilter = selectedProduct
                ? `Product: ${selectedProduct.product_name}`
                : selectedSubCategory
                    ? `Sub Category: ${selectedSubCategory}`
                    : 'All Products';

            const aoa = [
                ['Product-Based GRN Report'],
                [`Date Range: ${appliedFrom} to ${appliedTo}`],
                [`Filter: ${appliedFilter}`],
                [],
                header
            ];
            const merges = [];
            let totalQty = 0;
            let totalValue = 0;

            exportProducts.forEach(product => {
                totalQty += Number(product.total_qty_received) || 0;
                totalValue += Number(product.total_value) || 0;
                const groupStartRow = aoa.length; // 0-indexed sheet row, header already occupies row 0
                const groupLabel = product.short_name || product.subcategory_name || '-';

                product.grn_details.forEach((grn, idx) => {
                    aoa.push([
                        idx === 0 ? groupLabel : '',
                        idx === 0 ? product.total_grns : '',
                        grn.docno,
                        formatDate(grn.docdate),
                        grn.supplier_name || '-',
                        grn.qty,
                        formatAmount(grn.total_value)
                    ]);
                });

                if (product.grn_details.length > 1) {
                    const groupEndRow = groupStartRow + product.grn_details.length - 1;
                    merges.push(
                        { s: { r: groupStartRow, c: 0 }, e: { r: groupEndRow, c: 0 } },
                        { s: { r: groupStartRow, c: 1 }, e: { r: groupEndRow, c: 1 } }
                    );
                }
            });

            aoa.push(['', '', '', '', 'Grand Total', totalQty, Math.round(totalValue)]);

            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws['!merges'] = merges;
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Product GRN Report');
            XLSX.writeFile(wb, 'Product GRN Report.xlsx');
        } catch (error) {
            dispatch(setSnackbar(true, 'error', 'Failed to export report'));
        } finally {
            setExporting(false);
            setIsLoader(false);
        }
    };

    if (!isAuthorized) {
        return (
            <Paper style={{ padding: '30px', margin: '20px', textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                    Access Restricted
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    The Product-Based GRN Report is available to Admin / Super Admin users only.
                </Typography>
            </Paper>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Helmet><title>Product GRN Report</title></Helmet>
            <Loader isLoader={isLoader} />

            <Grid
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                }}
            >
                <Typography variant="h5" className="p-2">Product-Based GRN Report</Typography>

                <Grid container spacing={1.5} className="p-2 pt-2">
                    <Grid item md={2}>
                        <DatePicker
                            onChange={value => handleDateChange(value, 'fromDate')}
                            value={fromDate}
                            format="DD-MM-YYYY"
                            maxDate={toDate || undefined}
                            slotProps={{ textField: { size: 'small', label: 'From', style: { width: '100%' } } }}
                        />
                    </Grid>
                    <Grid item md={2}>
                        <DatePicker
                            onChange={value => handleDateChange(value, 'toDate')}
                            value={toDate}
                            format="DD-MM-YYYY"
                            minDate={fromDate || undefined}
                            slotProps={{ textField: { size: 'small', label: 'To', style: { width: '100%' } } }}
                        />
                    </Grid>
                    <Grid item md={2.5}>
                        <Autocomplete
                            size="small"
                            options={subCategoryOptions}
                            loading={optionsLoading}
                            value={selectedSubCategory}
                            onChange={handleSubCategory}
                            filterOptions={(options, state) => {
                                const input = state.inputValue.trim().toLowerCase();
                                if (!input) return options;
                                return options.filter(option => option.toLowerCase().includes(input));
                            }}
                            renderInput={params => <TextField {...params} label="Sub Category" />}
                        />
                    </Grid>
                    <Grid item md={2.5}>
                        <Autocomplete
                            size="small"
                            options={productOptions}
                            loading={optionsLoading}
                            value={selectedProduct}
                            onChange={handleProduct}
                            getOptionLabel={option => option.product_name || ''}
                            isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                            filterOptions={(options, state) => {
                                const input = state.inputValue.trim().toLowerCase();
                                if (!input) return options;
                                return options.filter(
                                    option =>
                                        option.product_name?.toLowerCase().includes(input) ||
                                        option.short_name?.toLowerCase().includes(input)
                                );
                            }}
                            renderInput={params => <TextField {...params} label="Product List" />}
                        />
                    </Grid>
                    <Grid item md={2} style={{ display: 'flex', alignItems: 'center' }}>
                        <Button variant="outlined" size="small" onClick={clearData}>
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <Grid className="text-end my-2" style={{ textAlign: 'right', padding: '0 20px' }}>
                <Button onClick={exportToExcel} variant="outlined" disabled={products.length === 0 || exporting}>
                    {exporting ? 'Exporting…' : 'Export to Excel'} <SimCardDownloadOutlinedIcon />
                </Button>
            </Grid>

            <Paper style={{ margin: '20px' }}>
                <TableContainer className={classes.tblRoot} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader size="small">
                        <TableHead className={classes.tblHeader} sx={{ position: 'sticky', top: 0 }}>
                            <TableRow className={classes.tablrow}>
                                <TableCell>Sub-Category / Short Name</TableCell>
                                <TableCell>Total GRNs</TableCell>
                                <TableCell>GRN Number</TableCell>
                                <TableCell>GRN Date</TableCell>
                                <TableCell>Supplier Name</TableCell>
                                <TableCell>Quantity Received</TableCell>
                                <TableCell>Total Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {searched ? 'No GRN records found' : 'No rows'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pagedProducts.map(product => (
                                    <Fragment key={product.product_id}>
                                        {product.grn_details.map((grn, idx) => (
                                            <TableRow className={classes.tablrow} key={`${product.product_id}-${grn.docno}-${idx}`}>
                                                {idx === 0 && (
                                                    <>
                                                        <TableCell rowSpan={product.grn_details.length}>
                                                            {product.short_name || product.subcategory_name}
                                                        </TableCell>
                                                        <TableCell rowSpan={product.grn_details.length}>
                                                            {product.total_grns}
                                                        </TableCell>
                                                    </>
                                                )}
                                                <TableCell>{grn.docno}</TableCell>
                                                <TableCell>{formatDate(grn.docdate)}</TableCell>
                                                <TableCell>{grn.supplier_name || '-'}</TableCell>
                                                <TableCell>{grn.qty}</TableCell>
                                                <TableCell>{formatAmount(grn.total_value)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </Fragment>
                                ))
                            )}
                        </TableBody>
                        {products.length > 0 && (
                            <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 1, backgroundColor: '#f3e5f5' }}>
                                <TableRow>
                                    <TableCell colSpan={5} align="right" style={{ fontWeight: 'bold' }}>
                                        Grand Total
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 'bold' }}>{grandTotalQty}</TableCell>
                                    <TableCell style={{ fontWeight: 'bold' }}>{Math.round(grandTotalValue)}</TableCell>
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </TableContainer>

                {products.length > 0 && (
                    <Grid container className={classes.footer}>
                        <Grid className={classes.paginationOne}>
                            <Autocomplete
                                disablePortal
                                id="page"
                                value={rowsPerPage}
                                onChange={(e, value) => {
                                    setRowsPerPage(value);
                                    setPage(0);
                                }}
                                options={pageOption}
                                getOptionLabel={option => option.toString()}
                                disableClearable
                                renderInput={params => (
                                    <TextField {...params} className={classes.paginationAutocomplete} disabled />
                                )}
                                size="small"
                            />
                            <Typography variant="body1" color="#ccc" ml="6px">
                                rows per page
                            </Typography>
                        </Grid>
                        <Grid>
                            <Pagination
                                color="primary"
                                size="small"
                                count={pageCount}
                                page={page + 1}
                                onChange={(event, newPage) => setPage(newPage - 1)}
                                boundaryCount={1}
                                renderItem={item => (
                                    <PaginationItem
                                        className={classes.pagination}
                                        slots={{ previous: ArrowLeftIcon, next: ArrowRightIcon }}
                                        {...item}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </LocalizationProvider>
    );
};

export default ProductGrnReport;
